import { DateTime, ExprCall, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { EncField, EncodingResult, SortType, VariableType } from '@mode-vegas/vegas';
import { EncodingHelpers, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, FormulaOperations, Switch } from '@mode/shared/util-switch';
import * as angular from 'angular';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { format, startOfWeek } from 'date-fns';
import { FieldComponent, FieldController } from './field.component';
import { ColumnRow, FieldIcons } from './viz-designer-icons';
import { first } from 'rxjs';

class ViewCalcFieldController extends FieldController {
  static $inject = ['$scope', '$element', 'ToastService', 'FeatureFlagsFacade'];

  // bindings
  view: FlamingoTypes.ModeView;
  allowScalars: 'true' | 'false' | undefined;
  allowAggregates: 'true' | 'false' | undefined;
  allowQuickCalcs: 'true' | 'false' | undefined;
  allowDiscrete: 'true' | 'false' | undefined;
  allowContinuous: 'true' | 'false' | undefined;
  allowNamesValues: 'true' | 'false' | undefined;

  onUpdate: (args: { $result: EncodingResult }) => void;
  onRemove: (args: { $field: EncField }) => void;
  onRemoveSort: (args: { $source: string }) => void;

  private customCalendarSettingsEnabled = false;

  constructor(
    readonly $scope: angular.IScope,
    private readonly $element: angular.IAugmentedJQuery,
    private readonly AlertService: any,
    private readonly featureFlagsFacade: FeatureFlagsFacade
  ) {
    super();
    this.isSortAllowed = true;
    this.handleContextSubMenuPositioning($scope);
    this.featureFlagsFacade
      .asObservable(FeatureFlag.CustomCalendar)
      .pipe(first())
      .subscribe((flag) => (this.customCalendarSettingsEnabled = flag));
  }

  /**
   * Better positioning for quick chart context menu submenus.
   * If it doesn't fit on the right, position it to the left
   */
  handleContextSubMenuPositioning($scope: angular.IScope) {
    type EventArg = {
      contextMenu: JQuery;
      params: {
        view: Window;
        level: number;
        event: {
          event: MouseEvent;
          pageX: number;
          pageY: number;
        };
      };
    };

    $scope.$on('context-menu-opened', onContextMenuOpened);
    function onContextMenuOpened(e: angular.IAngularEvent, { contextMenu, params }: EventArg) {
      // Bail if this isn't at our submenu depth
      if (params.level !== 1) return;

      // Bail if this isn't our menu
      if (!contextMenu.hasClass('context-menu-quick-charts')) return;
      contextMenu.addClass('qc-submenu');

      // Original mouse event
      const event = params.event.event;
      // Context menu item that triggered the submenu
      const target = angular.element(event.target).parents('li');
      // Bounding box of said context menu item, if we can get it
      const clientRect = target.get(0)?.getBoundingClientRect();

      // If we have a bounding box for the context menu item that triggered the submenu then we'll be able
      // to figure out how to position said submenu. If there isn't enough room to the right of the context
      // menu to position the submenu, ui.bootstrap.contextMenu will just overlay the context menu and it's
      // janky looking.. if there isn't enough room to the right, reposition it on the left (like @perfectmemory/ngx-contextmenu)
      if (clientRect) {
        const viewWidth = event.view.innerWidth + window.pageXOffset;
        const menuWidth = contextMenu.outerWidth();

        // Not enough room on the right, position to the left
        if (viewWidth - clientRect.right < menuWidth + 2) {
          params.event.pageX = clientRect.x - menuWidth - 1;
          params.event.pageY = clientRect.y;
        }
      }
    }
  }

  $onChanges(changes: angular.IOnChangesObject) {
    super.$onChanges(changes);

    if (changes.view) {
      if (this.view != null) {
        this.isValid = this.formula.isValid
          ? EncodingHelpers.validateDuplicateCalcFields(this.model, this.view.name, this.formula.attrName)
          : this.formula.isValid;
      }
    }

    // Validate sort encoding, if any
    if (changes.field && this.isValid) {
      const sortField = this.encoding.sorts.find((s) => s.source === this.field.formula);
      if (sortField && sortField.type === SortType.FIELD) {
        const errors: string[] = [];
        const schemaModel = SwitchSchemaModel.make(this.model, this.viewName);
        EncodingHelpers.validateEncSortField(sortField, schemaModel, errors);

        this.isValid = !errors.length;
        if (!this.isValid) {
          this.error = errors[0];
        }
      }
    }
  }

  $postLink() {
    this.$element[0].addEventListener('dragstart', (evt: any) => {
      if (evt.dataTransfer) {
        evt.dataTransfer.setData('text/plain', evt.target.id);
      }
    });
  }

  buildContextMenuOptions() {
    const opts = [];

    if (this.isNames || this.isValues) {
      if (this.hasSort) {
        opts.push(...this.buildSortMenuOpts());
      }

      if (this.allowNamesValues === 'true') {
        opts.push({
          html: this.getMenuItemHtml(false, 'Remove'),
          click: () => this.removeField(),
        });
      }
      return opts;
    }

    if (this.hasDateFuncMenuOpts) {
      opts.push(
        ...this.buildDateTruncMenuOpts(),
        ...this.buildDatePartMenuOpts(),
        ...this.buildYearStartMenuOpts(),
        ...this.buildWeekStartMenuOpts(),
        null
      );
    }

    if (this.hasExactDateTimeMenuOpt) {
      opts.push(...this.buildExactDateTimeMenuOpt());
    }

    if (this.hasNoAggregateMenuOpt) {
      opts.push(...this.buildNoAggregateMenuOpt());
    }

    if (this.hasSummableAggregateMenuOpts) {
      opts.push(...this.buildSummableAggregateMenuOpts());
    } else if (this.hasCountableAggregateMenuOpts) {
      opts.push(...this.buildCountableAggregateMenuOpts());
    }

    if (this.hasCalculationMenuOpts && (this.hasAnalyticFunc || this.isAggregateStage)) {
      opts.push(...this.buildCalculationMenuOpts());
    }

    if (this.hasRelativeToMenuOpts) {
      opts.push(...this.buildRelativeToMenuOpts());
    }

    if (this.hasVarTypeMenuOpts) {
      opts.push(...this.buildVarTypeMenuOpts());
    }

    if (this.hasSort) {
      opts.push(...this.buildSortMenuOpts());
    }

    opts.push({
      html: this.getMenuItemHtml(false, 'Remove'),
      click: () => this.removeField(),
    });

    return opts;
  }

  get hasSortMenuOpts(): boolean {
    return this.isNames || (this.isValues && this.hasSort);
  }

  buildSortMenuOpts(): any[] {
    return [
      {
        html: this.getMenuItemHtml(false, 'Clear sort'),
        click: () => this.removeSort(),
      },
    ];
  }

  get hasDateFuncMenuOpts(): boolean {
    return this.isValid && this.isDateExpr && this.allowScalars === 'true' && this.allowDiscrete === 'true';
  }

  buildDateTruncMenuOpts(): any[] {
    const date = new Date();

    const headerIcon =
      this.allowScalars === 'true' && this.allowAggregates === 'true'
        ? FieldIcons.continuousDate
        : FieldIcons.discreteDate;

    return [
      {
        html: this.getMenuHeadingHtml('Dates and Times', headerIcon),
        click: () => false,
        enabled: () => false,
      },
      {
        html: this.getMenuItemHtml(
          this.isDateTrunc && this.datePart === DateTime.Unit.Year,
          'Year',
          format(date, 'yyyy')
        ),
        click: () => this.setDateTrunc(DateTime.Unit.Year),
      },
      {
        html: this.getMenuItemHtml(
          this.isDateTrunc && this.datePart === DateTime.Unit.Quarter,
          'Quarter',
          format(date, 'QQQ yyyy')
        ),
        click: () => this.setDateTrunc(DateTime.Unit.Quarter),
      },
      {
        html: this.getMenuItemHtml(
          this.isDateTrunc && this.datePart === DateTime.Unit.Month,
          'Month',
          format(date, 'MMM yyyy')
        ),
        click: () => this.setDateTrunc(DateTime.Unit.Month),
      },
      {
        html: this.getMenuItemHtml(
          this.isDateTrunc && this.datePart === DateTime.Unit.Week,
          'Week',
          format(startOfWeek(date), 'MMM d yyyy')
        ),
        click: () => this.setDateTrunc(DateTime.Unit.Week),
      },
      {
        html: this.getMenuItemHtml(
          this.isDateTrunc && this.datePart === DateTime.Unit.Day,
          'Day',
          format(date, 'MMM d yyyy')
        ),
        click: () => this.setDateTrunc(DateTime.Unit.Day),
      },
      {
        html: this.getMenuItemHtml(false, 'More', '<span class="context-item-submenu-arrow">▶</span>'),
        children: [
          {
            html: this.getMenuItemHtml(
              this.isDateTrunc && this.datePart === DateTime.Unit.Hour,
              'Hour',
              format(date, 'haaa MMM d yyyy')
            ),
            click: () => this.setDateTrunc(DateTime.Unit.Hour),
          },
          {
            html: this.getMenuItemHtml(
              this.isDateTrunc && this.datePart === DateTime.Unit.Minute,
              'Minute',
              format(date, 'h:mmaaa MMM d yyyy')
            ),
            click: () => this.setDateTrunc(DateTime.Unit.Minute),
          },
          {
            html: this.getMenuItemHtml(
              this.isDateTrunc && this.datePart === DateTime.Unit.Second,
              'Second',
              format(date, 'h:mm:ssaaa MMM d yyyy')
            ),
            click: () => this.setDateTrunc(DateTime.Unit.Second),
          },
        ],
      },
      null,
    ];
  }

  buildDatePartMenuOpts(): any[] {
    const date = new Date();

    return [
      {
        html: this.getMenuHeadingHtml('Date Parts', FieldIcons.discreteDate),
        click: () => false,
        enabled: () => false,
      },
      {
        html: this.getMenuItemHtml(this.isDatePart && this.datePart === DateTime.Unit.Year, 'Year', format(date, 'y')),
        click: () => this.setDatePart(DateTime.Unit.Year),
      },
      {
        html: this.getMenuItemHtml(
          this.isDatePart && this.datePart === DateTime.Unit.Quarter,
          'Quarter',
          format(date, 'QQQ')
        ),
        click: () => this.setDatePart(DateTime.Unit.Quarter),
      },
      {
        html: this.getMenuItemHtml(
          this.isDatePart && this.datePart === DateTime.Unit.Month,
          'Month',
          format(date, 'MMM')
        ),
        click: () => this.setDatePart(DateTime.Unit.Month),
      },
      {
        html: this.getMenuItemHtml(
          this.isDatePart && this.datePart === DateTime.Unit.Weekday,
          'Weekday',
          format(date, 'ccc')
        ),
        click: () => this.setDatePart(DateTime.Unit.Weekday),
      },
      {
        html: this.getMenuItemHtml(this.isDatePart && this.datePart === DateTime.Unit.Day, 'Day', format(date, 'd')),
        click: () => this.setDatePart(DateTime.Unit.Day),
      },
      {
        html: this.getMenuItemHtml(false, 'More', '<span class="context-item-submenu-arrow">▶</span>'),
        children: [
          {
            html: this.getMenuItemHtml(
              this.isDatePart && this.datePart === DateTime.Unit.Hour,
              'Hour',
              format(date, 'h')
            ),
            click: () => this.setDatePart(DateTime.Unit.Hour),
          },
          {
            html: this.getMenuItemHtml(
              this.isDatePart && this.datePart === DateTime.Unit.Minute,
              'Minute',
              format(date, 'm')
            ),
            click: () => this.setDatePart(DateTime.Unit.Minute),
          },
          {
            html: this.getMenuItemHtml(
              this.isDatePart && this.datePart === DateTime.Unit.Second,
              'Second',
              format(date, 's')
            ),
            click: () => this.setDatePart(DateTime.Unit.Second),
          },
        ],
      },
      null,
    ];
  }

  buildWeekStartMenuOpts() {
    const weekStarts = [
      { value: DateTime.WeekStart.Sunday, name: 'Sunday' },
      { value: DateTime.WeekStart.Monday, name: 'Monday' },
      { value: DateTime.WeekStart.Tuesday, name: 'Tuesday' },
      { value: DateTime.WeekStart.Wednesday, name: 'Wednesday' },
      { value: DateTime.WeekStart.Thursday, name: 'Thursday' },
      { value: DateTime.WeekStart.Friday, name: 'Friday' },
      { value: DateTime.WeekStart.Saturday, name: 'Saturday' },
    ];

    return [
      {
        html: this.getMenuItemHtml(false, 'Week start', '<span class="context-item-submenu-arrow">▶</span>'),
        children: weekStarts.map(({ value, name }) => {
          const asideText =
            this.customCalendarSettingsEnabled && this.defaultWeekStart === value ? 'Org Default' : undefined;
          return {
            html: this.getMenuItemHtml(this.weekStart === value, name, asideText),
            click: () => this.setWeekStart(value),
          };
        }),
      },
    ];
  }

  buildYearStartMenuOpts() {
    const yearStarts = [
      { value: DateTime.YearStart.January, name: 'January' },
      { value: DateTime.YearStart.February, name: 'February' },
      { value: DateTime.YearStart.March, name: 'March' },
      { value: DateTime.YearStart.April, name: 'April' },
      { value: DateTime.YearStart.May, name: 'May' },
      { value: DateTime.YearStart.June, name: 'June' },
      { value: DateTime.YearStart.July, name: 'July' },
      { value: DateTime.YearStart.August, name: 'August' },
      { value: DateTime.YearStart.September, name: 'September' },
      { value: DateTime.YearStart.October, name: 'October' },
      { value: DateTime.YearStart.November, name: 'November' },
      { value: DateTime.YearStart.December, name: 'December' },
    ];

    return [
      {
        html: this.getMenuItemHtml(false, 'Year start', '<span class="context-item-submenu-arrow">▶</span>'),
        children: yearStarts.map(({ value, name }) => {
          const asideText =
            this.customCalendarSettingsEnabled && this.defaultYearStart === value ? 'Org Default' : undefined;
          return {
            html: this.getMenuItemHtml(this.yearStart === value, name, asideText),
            click: () => this.setYearStart(value),
          };
        }),
      },
    ];
  }

  get hasExactDateTimeMenuOpt(): boolean {
    return this.isValid && this.isDateExpr && this.allowScalars === 'true';
  }

  buildExactDateTimeMenuOpt(): any[] {
    return [
      {
        html: this.getMenuItemHtml(this.isExactDateAndTime, 'Exact date and time', '', 'no-max-width'),
        click: () => this.setAggregate(null, this.allowContinuous === 'true'),
      },
      null,
    ];
  }

  get hasNoAggregateMenuOpt(): boolean {
    return (
      this.isValid &&
      !this.isDateExpr && /// Use Exact Date/time instead
      !this.formula.isNonScalarCalc &&
      this.allowScalars === 'true' &&
      this.allowAggregates === 'true'
    );
  }

  buildNoAggregateMenuOpt(): any[] {
    return [
      {
        html: this.getMenuItemHtml(!this.aggFunc, 'No aggregate'),
        click: () => this.setAggregate(null),
      },
      null,
    ];
  }

  get hasSummableAggregateMenuOpts(): boolean {
    return this.isValid && this.isSummable && this.allowAggregates === 'true';
  }

  buildSummableAggregateMenuOpts(): any[] {
    return [
      {
        html: this.getMenuItemHtml(this.aggFunc === 'SUM', 'Sum'),
        click: () => this.setAggregate(ExprCall.Name.Sum),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNT', 'Count'),
        click: () => this.setAggregate(ExprCall.Name.Count),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNTD', 'Count (distinct)'),
        click: () => this.setAggregate(ExprCall.Name.CountD),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNTDA', 'Approx. count (distinct)', '', 'no-max-width'),
        click: () => this.setAggregate(ExprCall.Name.CountDA),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'AVG', 'Average'),
        click: () => this.setAggregate(ExprCall.Name.Avg),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'MAX', 'Maximum'),
        click: () => this.setAggregate(ExprCall.Name.Max),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'MIN', 'Minimum'),
        click: () => this.setAggregate(ExprCall.Name.Min),
      },
      null,
    ];
  }

  get hasCountableAggregateMenuOpts(): boolean {
    return this.isValid && !this.isSummable && this.isCountable && this.allowAggregates === 'true';
  }

  buildCountableAggregateMenuOpts(): any[] {
    return [
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNT', 'Count'),
        click: () => this.setAggregate(ExprCall.Name.Count, true),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNTD', 'Count (distinct)'),
        click: () => this.setAggregate(ExprCall.Name.CountD, true),
      },
      {
        html: this.getMenuItemHtml(this.aggFunc === 'COUNTDA', 'Approx. count (distinct)', '', 'no-max-width'),
        click: () => this.setAggregate(ExprCall.Name.CountDA, true),
      },
      null,
    ];
  }

  get hasVarTypeMenuOpts(): boolean {
    return (
      this.isValid &&
      this.canBeDiscrete &&
      this.canBeContinuous &&
      this.allowDiscrete === 'true' &&
      this.allowContinuous === 'true'
    );
  }

  buildVarTypeMenuOpts(): any[] {
    return [
      {
        html: this.getMenuItemHtml(this.formula.isDiscrete, 'Discrete'),
        click: () => this.setVariableType(VariableType.DISCRETE),
      },
      {
        html: this.getMenuItemHtml(this.formula.isContinuous, 'Continuous'),
        click: () => this.setVariableType(VariableType.CONTINUOUS),
      },
      null,
    ];
  }

  get hasCalculationMenuOpts(): boolean {
    return this.isValid && this.allowQuickCalcs === 'true';
  }

  get hasAnalyticFunc(): boolean | undefined {
    return this.formula?.hasAnalyticFunc;
  }

  get isAggregateStage(): boolean | undefined {
    return this.formula?.isAggregateStage;
  }

  buildCalculationMenuOpts(): any[] {
    return [
      {
        html: this.getMenuHeadingHtml('Indicator'),
        click: () => false,
        enabled: () => false,
      },
      {
        html: this.getMenuItemHtml(this.formType === ExprForm.PctDifference, 'Percent difference'),
        click: () => this.setTrend(ExprForm.PctDifference),
      },
      {
        html: this.getMenuItemHtml(this.formType === ExprForm.Difference, 'Difference'),
        click: () => this.setTrend(ExprForm.Difference),
      },
      {
        html: this.getMenuItemHtml(!this.formula.hasAnalyticFunc, "Don't perform calculation"),
        click: () => this.setTrend(null),
      },
      null,
    ];
  }

  get hasRelativeToMenuOpts(): boolean {
    if (this.formula == null) {
      return false;
    }

    switch (this.formula.form) {
      case ExprForm.Difference:
      case ExprForm.PctDifference:
        return true;
      default:
        return false;
    }
  }

  buildRelativeToMenuOpts(): any[] {
    return [
      {
        html: this.getMenuHeadingHtml('Relative To'),
        click: () => false,
        enabled: () => false,
      },
      {
        html: this.getMenuItemHtml(this.relativeTo === ExprForm.RelativeTo.Prev, 'Previous value'),
        click: () => this.setRelativeTo(ExprForm.RelativeTo.Prev),
        enabled: () => this.formType !== ExprForm.Aggregate,
      },
      {
        html: this.getMenuItemHtml(this.relativeTo === ExprForm.RelativeTo.First, 'First value'),
        click: () => this.setRelativeTo(ExprForm.RelativeTo.First),
        enabled: () => this.formType !== ExprForm.Aggregate,
      },
      null,
    ];
  }

  removeSort(): void {
    this.onRemoveSort({ $source: this.formula.formula });
  }

  removeField(): void {
    this.onRemove({ $field: this.field });
  }

  dispatchEncodingUpdate(result: EncodingResult) {
    this.onUpdate({ $result: result });
  }

  setAggregate(aggregate: ExprCall.Name | null, continuous: boolean | undefined = undefined) {
    if (this.formula == null) {
      return;
    }

    const newFormula =
      aggregate != null ? FormulaOperations.aggregate(this.formula, aggregate) : FormulaOperations.access(this.formula);

    if (!this.formula.hasAnalyticFunc) {
      const fieldEnc = this.formula
        .withNewFormula(newFormula, continuous != null ? Switch.Compat.boolToAttrType(continuous) : undefined)
        .toModifyField();
      const exists = this.encoding.value.some((v) => v.formula === newFormula);

      if (!exists) {
        this.dispatchEncodingUpdate(EncodingHelpers.updateFieldEncoding(this.encoding, fieldEnc));
      } else {
        this.AlertService.error(`
            Y-Axis already uses ${newFormula}.
            To use fields more than once they must have unique aggregates.
          `);
      }
    } else {
      let source: string;
      switch (this.formula.form) {
        case ExprForm.Difference: {
          source = FormulaOperations.diff(this.formula, aggregate);
          break;
        }
        case ExprForm.PctDifference: {
          source = FormulaOperations.pctDiff(this.formula, aggregate);
          break;
        }
      }

      if (source != null) {
        this.dispatchEncodingUpdate(
          EncodingHelpers.updateFieldEncoding(this.encoding, {
            id: this.field.id,
            formula: source,
            variable:
              continuous === undefined
                ? this.field.variable
                : { type: continuous ? VariableType.CONTINUOUS : VariableType.DISCRETE },
          })
        );
      }
    }
  }

  setDatePart(part: DateTime.Unit) {
    if (this.formula == null) {
      return;
    }

    const newField = this.formula.withNewFormula(FormulaOperations.datePart(this.formula, part));
    this.dispatchEncodingUpdate(EncodingHelpers.updateFieldEncoding(this.encoding, newField.toModifyField()));
  }

  setDateTrunc(part: DateTime.Unit) {
    if (this.formula == null) {
      return;
    }

    const newField = this.formula.withNewFormula(FormulaOperations.dateTrunc(this.formula, part));
    this.dispatchEncodingUpdate(EncodingHelpers.updateFieldEncoding(this.encoding, newField.toModifyField()));
  }

  setVariableType(type: VariableType) {
    this.dispatchEncodingUpdate(
      EncodingHelpers.updateFieldEncoding(this.encoding, {
        id: this.field.id,
        variable: { type },
      })
    );
  }

  setWeekStart(weekStart: DateTime.WeekStart): void {
    this.setWeekOrYearStart(weekStart, undefined);
  }

  setYearStart(yearStart: DateTime.YearStart): void {
    this.setWeekOrYearStart(this.weekStart, yearStart);
  }

  setWeekOrYearStart(weekStart?: DateTime.WeekStart, yearStart?: DateTime.YearStart): void {
    let newField: CompiledField | undefined;

    if (this.formula?.dateFuncUnit) {
      const dateFunc = this.isDateTrunc ? FormulaOperations.dateTrunc : FormulaOperations.datePart;
      newField = this.formula.withNewFormula(
        dateFunc(this.formula, this.formula.dateFuncUnit, weekStart, yearStart),
        this.formula.varType
      );
    } else if (this.formula?.isAttrDateType) {
      const dateFuncUnit = weekStart ? DateTime.Unit.Week : DateTime.Unit.Year;
      newField = this.formula.withNewFormula(
        FormulaOperations.dateTrunc(this.formula, dateFuncUnit, weekStart, yearStart),
        this.allowContinuous ? Attr.Type.Continuous : Attr.Type.Discrete
      );
    }

    if (newField) {
      const encResult = EncodingHelpers.updateFieldEncoding(this.encoding, newField.toModifyField());
      this.dispatchEncodingUpdate(encResult);
    }
  }

  setTrend(trend: ExprForm.PctDifference | ExprForm.Difference | null) {
    if (this.formula == null) {
      return;
    }

    let source: string | null = null;
    switch (trend) {
      case ExprForm.Difference:
        source = FormulaOperations.diff(this.formula);
        break;
      case ExprForm.PctDifference:
        source = FormulaOperations.pctDiff(this.formula);
        break;
      default:
        source = FormulaOperations.clearAnalytic(this.formula);
        break;
    }

    if (source != null) {
      this.dispatchEncodingUpdate(
        EncodingHelpers.updateFieldEncoding(this.encoding, {
          id: this.field.id,
          formula: source,
        })
      );
    }
  }

  setRelativeTo(relativeTo: ExprForm.RelativeTo) {
    if (this.formula == null) {
      return;
    }

    const newFormula = this.formula.withNewFormula(FormulaOperations.relativeTo(this.formula, relativeTo));
    this.dispatchEncodingUpdate(EncodingHelpers.updateFieldEncoding(this.encoding, newFormula.toModifyField()));
  }

  get formType(): ExprForm {
    return this.formula.form;
  }

  get relativeTo(): ExprForm.RelativeTo {
    return this.formula.relativeTo;
  }

  get canToggleDimension(): boolean {
    return !(this.isNames || this.isValues);
  }

  get datePart(): DateTime.Unit {
    return this.formula.dateFuncUnit;
  }

  get isAccess(): boolean {
    return this.formula.form === ExprForm.Access;
  }

  get isAggregate(): boolean {
    return this.formula.form === ExprForm.Aggregate;
  }

  get isDatePart(): boolean {
    return this.formula.form === ExprForm.DatePart;
  }

  get isDateTrunc(): boolean {
    return this.formula.form === ExprForm.DateTrunc;
  }

  get hasDateFunc(): boolean {
    return this.isDatePart || this.isDateTrunc;
  }

  get isDateExpr(): boolean {
    return this.isFromDateType || this.hasDateFunc;
  }

  get weekStart(): DateTime.WeekStart | undefined {
    if (this.hasDateFunc) {
      return this.formula.weekStart ?? this.defaultWeekStart;
    }

    return;
  }

  get yearStart(): DateTime.YearStart | undefined {
    if (this.hasDateFunc) {
      return this.formula.yearStart ?? this.defaultYearStart;
    }

    return;
  }

  get defaultWeekStart(): DateTime.WeekStart | undefined {
    return this.formula.defaultWeekStart;
  }

  get defaultYearStart(): DateTime.YearStart | undefined {
    return this.formula.defaultYearStart;
  }

  get canBeDiscrete(): boolean {
    return true;
  }

  get canBeContinuous(): boolean {
    return this.isNumericType || this.isDateTimeType;
  }

  get isCountable(): boolean {
    if (this.formula == null) {
      return false;
    }

    return this.formula.isValid && !this.formula.isNonScalarCalc; // calculated fields with aggregates cannot be aggregated
  }

  get isSummable(): boolean {
    if (this.formula == null) {
      return false;
    }

    return this.isFromNumberType && !this.isFromDateType && !this.formula.isNonScalarCalc; // calculated fields with aggregates cannot be aggregated
  }

  get isExactDateAndTime(): boolean {
    if (this.formula == null) {
      return;
    }

    switch (this.formula.form) {
      case ExprForm.Aggregate:
        return false;
      case ExprForm.DatePart:
      case ExprForm.DateTrunc:
        return false;
      default:
        return this.isFromDateType;
    }
  }

  get aggFunc(): string {
    if (this.formula == null) {
      return;
    }

    return (this.formula.aggFunc || '').toUpperCase();
  }

  get isNumericType(): boolean {
    return this.formula.isFormulaNumeric;
  }

  get isDateTimeType(): boolean {
    return this.formula.hasDateFunc;
  }

  get isFromNumberType(): boolean {
    return this.formula.isAttrNumberType;
  }

  get isFromDateType(): boolean {
    return this.formula.isAttrDateType;
  }

  get sortDirectionIcon(): string {
    if (this.encoding && this.encoding.sorts) {
      const sort = this.encoding.sorts.filter((s) => s.source === this.field.formula)[0];
      if (sort && this.formula) {
        if (this.formula.isNames) {
          return sort.descending ? ColumnRow.sortAscNames : ColumnRow.sortDescNames;
        }

        if (this.formula.isDiscrete) {
          return sort.descending ? ColumnRow.sortDescDiscrete : ColumnRow.sortAscDiscrete;
        }
      }
    }

    return null;
  }
}

export const ViewCalcFieldComponent: angular.IComponentOptions = {
  ...FieldComponent,
  bindings: {
    ...FieldComponent.bindings,
    allowScalars: '@',
    allowAggregates: '@',
    allowQuickCalcs: '@',
    allowContinuous: '@',
    allowDiscrete: '@',
    allowNamesValues: '@',
    onUpdate: '&',
    onRemove: '&',
    onRemoveSort: '&',
    view: '<',
  },
  controller: ViewCalcFieldController,
};
